/// <reference no-default-lib="true" />
/// <reference lib="esnext" />
/// <reference lib="dom" />

import {
  animateEnter,
  animateLeave,
  prepareEnter,
} from '../../design/animation.ts'

const DESKTOP_MAX_EXPANSIONS = 3

function openDrawerNonModal(drawer: HTMLDialogElement): void {
  drawer.show()
  const main = document.querySelector('main.layout') as HTMLElement | null
  if (main) {
    main.inert = true
  }
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      drawer.close()
    }
  }, { once: true })
}

export class MobileHeader {
  private isDesktop: MediaQueryList
  private header: HTMLElement & { showPopover: () => void }
  private navigationDrawer: HTMLDialogElement | null
  private navigationDrawerToggle: HTMLElement | null
  private navigationDrawerToggleIcon: HTMLElement | null
  private navigationDrawerToggleOpenLabel: string = ''
  private navigationList: HTMLElement | null
  private navigationDrawerOpenHandler: (() => void) | null = null
  private navigationDrawerCloseHandler: (() => void) | null = null
  private navigationButtonHandlers: Map<HTMLButtonElement, () => void> =
    new Map()
  private searchDrawer: HTMLDialogElement | null
  private searchDrawerToggle: HTMLElement | null
  private searchDrawerToggleIcon: HTMLElement | null
  private searchDrawerToggleOpenLabel: string = ''
  private searchDrawerOpenHandler: (() => void) | null = null
  private searchDrawerCloseHandler: (() => void) | null = null
  private cartDrawer: HTMLDialogElement | null
  private cartDrawerToggle: HTMLElement | null
  private cartDrawerToggleIcon: HTMLElement | null
  private cartDrawerToggleOpenLabel: string = ''
  private cartDrawerBadge: HTMLElement | null
  private cartDrawerOpenHandler: (() => void) | null = null
  private cartDrawerCloseHandler: (() => void) | null = null
  private drawerEventHandler: ((e: Event) => void) | null = null

  constructor(headerElement: HTMLElement & { showPopover: () => void }) {
    this.isDesktop = window.matchMedia(`(min-width: ${BREAKPOINT}px)`)
    this.header = headerElement
    this.navigationDrawer = document.querySelector(
      '#header-navigation-drawer',
    ) as
      | HTMLDialogElement
      | null
    this.navigationDrawerToggle = this.header?.querySelector(
      '.header__mobile-navigation-drawer-toggle',
    ) as
      | HTMLElement
      | null
    this.navigationDrawerToggleIcon = this.navigationDrawerToggle
      ?.querySelector(
        '.icon__toggle',
      ) as
        | HTMLElement
        | null
    this.navigationList = this.navigationDrawer?.querySelector(
      '.header__navigation-list',
    ) as HTMLElement | null
    this.searchDrawer = document.querySelector(
      '#header-search-drawer',
    ) as
      | HTMLDialogElement
      | null
    this.searchDrawerToggle = this.header?.querySelector(
      '.header__mobile-search-drawer-toggle',
    ) as
      | HTMLElement
      | null
    this.searchDrawerToggleIcon = this.searchDrawerToggle?.querySelector(
      '.icon__toggle',
    ) as
      | HTMLElement
      | null
    this.cartDrawer = document.querySelector(
      '#header-cart-drawer',
    ) as
      | HTMLDialogElement
      | null
    this.cartDrawerToggle = this.header?.querySelector(
      '.header__mobile-cart-drawer-toggle',
    ) as
      | HTMLElement
      | null
    this.cartDrawerToggleIcon = this.cartDrawerToggle?.querySelector(
      '.icon__toggle',
    ) as
      | HTMLElement
      | null
    this.cartDrawerBadge = this.cartDrawerToggle?.querySelector(
      '.button__badge',
    ) as
      | HTMLElement
      | null

    if (this.navigationDrawer === null || this.navigationList === null) {
      return
    }

    this.init()
  }

  private init(): void {
    this.navigationDrawerToggleOpenLabel =
      this.navigationDrawerToggle?.getAttribute('aria-label') ?? ''
    this.searchDrawerToggleOpenLabel =
      this.searchDrawerToggle?.getAttribute('aria-label') ?? ''
    this.cartDrawerToggleOpenLabel =
      this.cartDrawerToggle?.getAttribute('aria-label') ?? ''

    this.setupNavigationDrawerEventListeners()
    this.setupSearchDrawerEventListeners()
    this.setupCartDrawerEventListeners()
    this.setupNavigationItemEventListeners()
    this.setupDrawerEventApi()
    this.navigationDrawerToggleIcon?.dispatchEvent(
      new CustomEvent('toggle-default'),
    )
    this.searchDrawerToggleIcon?.dispatchEvent(
      new CustomEvent('toggle-default'),
    )
    this.cartDrawerToggleIcon?.dispatchEvent(
      new CustomEvent('toggle-default'),
    )
  }

  private setupNavigationDrawerEventListeners(): void {
    this.navigationDrawerOpenHandler = () => {
      this.navigationDrawerToggle?.setAttribute('command', 'close')
      this.navigationDrawerToggle?.setAttribute(
        'aria-label',
        this.navigationDrawerToggle.dataset.ariaLabelClose ?? '',
      )
      this.navigationDrawerToggle?.setAttribute('aria-expanded', 'true')
      this.navigationDrawerToggleIcon?.dispatchEvent(
        new CustomEvent('toggle-toggle'),
      )
      if (this.searchDrawer?.open) {
        this.searchDrawer.close()
      }
      if (this.cartDrawer?.open) {
        this.cartDrawer.close()
      }
    }

    this.navigationDrawerCloseHandler = () => {
      this.navigationDrawerToggle?.setAttribute('command', '--show')
      this.navigationDrawerToggle?.setAttribute(
        'aria-label',
        this.navigationDrawerToggleOpenLabel,
      )
      this.navigationDrawerToggle?.setAttribute('aria-expanded', 'false')
      this.navigationDrawerToggleIcon?.dispatchEvent(
        new CustomEvent('toggle-default'),
      )
      this.reset()
    }

    this.navigationDrawer?.addEventListener(
      'open',
      this.navigationDrawerOpenHandler,
    )
    this.navigationDrawer?.addEventListener(
      'close',
      this.navigationDrawerCloseHandler,
    )
  }

  private setupSearchDrawerEventListeners(): void {
    this.searchDrawerOpenHandler = () => {
      this.searchDrawerToggle?.setAttribute('command', 'close')
      this.searchDrawerToggle?.setAttribute(
        'aria-label',
        this.searchDrawerToggle.dataset.ariaLabelClose ?? '',
      )
      this.searchDrawerToggle?.setAttribute('aria-expanded', 'true')
      this.searchDrawerToggleIcon?.dispatchEvent(
        new CustomEvent('toggle-toggle'),
      )
      if (this.navigationDrawer?.open) {
        this.navigationDrawer.close()
      }
      if (this.cartDrawer?.open) {
        this.cartDrawer.close()
      }
    }

    this.searchDrawerCloseHandler = () => {
      this.searchDrawerToggle?.setAttribute('command', '--show')
      this.searchDrawerToggle?.setAttribute(
        'aria-label',
        this.searchDrawerToggleOpenLabel,
      )
      this.searchDrawerToggle?.setAttribute('aria-expanded', 'false')
      this.searchDrawerToggleIcon?.dispatchEvent(
        new CustomEvent('toggle-default'),
      )
    }

    this.searchDrawer?.addEventListener(
      'open',
      this.searchDrawerOpenHandler,
    )
    this.searchDrawer?.addEventListener(
      'close',
      this.searchDrawerCloseHandler,
    )
  }

  private setupCartDrawerEventListeners(): void {
    this.cartDrawerOpenHandler = () => {
      this.cartDrawerToggle?.setAttribute('command', 'close')
      this.cartDrawerToggle?.setAttribute(
        'aria-label',
        this.cartDrawerToggle.dataset.ariaLabelClose ?? '',
      )
      this.cartDrawerToggle?.setAttribute('aria-expanded', 'true')
      this.cartDrawerToggleIcon?.dispatchEvent(
        new CustomEvent('toggle-toggle'),
      )
      if (this.navigationDrawer?.open) {
        this.navigationDrawer.close()
      }
      if (this.searchDrawer?.open) {
        this.searchDrawer.close()
      }
      this.cartDrawerBadge?.style.setProperty('display', 'none')
    }

    this.cartDrawerCloseHandler = () => {
      this.cartDrawerToggle?.setAttribute('command', '--show')
      this.cartDrawerToggle?.setAttribute(
        'aria-label',
        this.cartDrawerToggleOpenLabel,
      )
      this.cartDrawerToggle?.setAttribute('aria-expanded', 'false')
      this.cartDrawerToggleIcon?.dispatchEvent(
        new CustomEvent('toggle-default'),
      )
      this.cartDrawerBadge?.style.setProperty('display', '')
    }

    this.cartDrawer?.addEventListener(
      'open',
      this.cartDrawerOpenHandler,
    )
    this.cartDrawer?.addEventListener(
      'close',
      this.cartDrawerCloseHandler,
    )
  }

  private setupNavigationItemEventListeners(): void {
    const navigationItemButtons = Array.from(
      this.navigationDrawer!.querySelectorAll(
        'button.header__navigation-item-link',
      ),
    )

    for (const navigationItemButton of navigationItemButtons) {
      if (!(navigationItemButton instanceof HTMLButtonElement)) {
        continue
      }

      const handler = async () => {
        const navigationItem = navigationItemButton.closest(
          '.header__navigation-item',
        )

        if (navigationItem && this.navigationList) {
          await animateLeave(this.navigationList)
          navigationItem.classList.add('header__navigation-item--active')

          const subnavList = navigationItem.querySelector(
            '.header__navigation-item-list',
          )
          const firstFocusable = subnavList?.querySelector(
            '.header__navigation-item-link',
          )
          if (firstFocusable instanceof HTMLElement) {
            firstFocusable.focus()
          }

          await animateEnter(this.navigationList)

          const back = navigationItem?.querySelector(
            '.header__navigation-item-back',
          )
          if (back) {
            back.addEventListener('click', async () => {
              await animateLeave(this.navigationList!)
              navigationItem.classList.remove('header__navigation-item--active')

              // Restore focus to original parent button
              const parentButton = navigationItem.querySelector(
                'button.header__navigation-item-link',
              )
              if (parentButton instanceof HTMLElement) {
                parentButton.focus()
              }

              await animateEnter(this.navigationList!)
            }, { once: true })
          }
        }
      }

      this.navigationButtonHandlers.set(navigationItemButton, handler)
      navigationItemButton.addEventListener('click', handler)
    }
  }

  private reset() {
    if (!this.navigationDrawer || !this.navigationList) {
      return
    }

    const activeNavigationItems = Array.from(
      this.navigationDrawer.querySelectorAll(
        '.header__navigation-item--active',
      ),
    )

    for (const activeNavigationItem of activeNavigationItems) {
      if (activeNavigationItem instanceof HTMLElement) {
        activeNavigationItem.classList.remove('header__navigation-item--active')
      }
    }

    this.navigationList.querySelector('.header__navigation-item')?.classList
      .add('header__navigation-item--active')
  }

  private setupDrawerEventApi(): void {
    this.drawerEventHandler = (e: Event) => {
      if (!(e instanceof CustomEvent)) return
      const name = e.detail?.drawer
      let drawer: HTMLDialogElement | null = null
      if (name === 'navigation') drawer = this.navigationDrawer
      else if (name === 'search') drawer = this.searchDrawer
      else if (name === 'cart') drawer = this.cartDrawer
      if (!drawer) return
      if (e.type === 'header:drawer-open') {
        openDrawerNonModal(drawer)
      } else if (e.type === 'header:drawer-close') {
        drawer.close()
      }
    }
    this.header.addEventListener(
      'header:drawer-open',
      this.drawerEventHandler,
    )
    this.header.addEventListener(
      'header:drawer-close',
      this.drawerEventHandler,
    )
  }

  destroy(): void {
    if (this.navigationDrawer) {
      if (this.navigationDrawerOpenHandler) {
        this.navigationDrawer.removeEventListener(
          'open',
          this.navigationDrawerOpenHandler,
        )
      }
      if (this.navigationDrawerCloseHandler) {
        this.navigationDrawer.removeEventListener(
          'close',
          this.navigationDrawerCloseHandler,
        )
      }
      this.navigationDrawer.close()
    }

    if (this.searchDrawer) {
      if (this.searchDrawerOpenHandler) {
        this.searchDrawer.removeEventListener(
          'open',
          this.searchDrawerOpenHandler,
        )
      }
      if (this.searchDrawerCloseHandler) {
        this.searchDrawer.removeEventListener(
          'close',
          this.searchDrawerCloseHandler,
        )
      }
      this.searchDrawer.close()
    }

    if (this.cartDrawer) {
      if (this.cartDrawerOpenHandler) {
        this.cartDrawer.removeEventListener(
          'open',
          this.cartDrawerOpenHandler,
        )
      }
      if (this.cartDrawerCloseHandler) {
        this.cartDrawer.removeEventListener(
          'close',
          this.cartDrawerCloseHandler,
        )
      }
      this.cartDrawer.close()
    }

    this.navigationDrawerToggle?.setAttribute('command', '--show')
    this.navigationDrawerToggleIcon?.dispatchEvent(
      new CustomEvent('toggle-default'),
    )
    this.searchDrawerToggle?.setAttribute('command', '--show')
    this.searchDrawerToggleIcon?.dispatchEvent(
      new CustomEvent('toggle-default'),
    )
    this.cartDrawerToggle?.setAttribute('command', '--show')
    this.cartDrawerToggleIcon?.dispatchEvent(
      new CustomEvent('toggle-default'),
    )
    this.cartDrawerBadge?.style.setProperty('display', '')

    for (const [button, handler] of this.navigationButtonHandlers) {
      button.removeEventListener('click', handler)
    }

    this.navigationButtonHandlers.clear()
    this.navigationDrawerOpenHandler = null
    this.navigationDrawerCloseHandler = null
    this.searchDrawerOpenHandler = null
    this.searchDrawerCloseHandler = null
    this.cartDrawerOpenHandler = null
    this.cartDrawerCloseHandler = null

    if (this.drawerEventHandler) {
      this.header.removeEventListener(
        'header:drawer-open',
        this.drawerEventHandler,
      )
      this.header.removeEventListener(
        'header:drawer-close',
        this.drawerEventHandler,
      )
      this.drawerEventHandler = null
    }
  }
}

export class DesktopHeader {
  private header: HTMLElement & { showPopover: () => void }
  private navigationDrawer: HTMLDialogElement | null
  private navigationDrawerBody: HTMLDivElement | null
  private navigationDrawerToggle: HTMLElement | null
  private navigationDrawerToggleIcon: HTMLElement | null
  private navigationList: HTMLElement | null
  private navigationButtonHandlers: Map<HTMLButtonElement, () => void> =
    new Map()
  private expansions: HTMLUListElement[]
  private searchDrawer: HTMLDialogElement | null
  private cartDrawer: HTMLDialogElement | null
  private drawerEventHandler: ((e: Event) => void) | null = null

  constructor(headerElement: HTMLElement & { showPopover: () => void }) {
    this.header = headerElement
    this.navigationDrawer = document.querySelector(
      '#header-navigation-drawer',
    ) as
      | HTMLDialogElement
      | null
    this.navigationDrawerBody = document.querySelector(
      '#header-navigation-drawer .drawer__body',
    ) as
      | HTMLDivElement
      | null
    this.navigationDrawerToggle = this.header?.querySelector(
      '.header__mobile-navigation-drawer-toggle',
    ) as
      | HTMLElement
      | null
    this.navigationDrawerToggleIcon = this.navigationDrawerToggle
      ?.querySelector(
        '.icon__toggle',
      ) as
        | HTMLElement
        | null
    this.navigationList = this.navigationDrawer?.querySelector(
      '.header__navigation-list',
    ) as HTMLElement | null
    this.searchDrawer = document.querySelector(
      '#header-search-drawer',
    ) as
      | HTMLDialogElement
      | null
    this.cartDrawer = document.querySelector(
      '#header-cart-drawer',
    ) as
      | HTMLDialogElement
      | null

    this.expansions = []

    if (
      this.navigationDrawer === null || this.navigationList === null ||
      this.navigationDrawerBody === null
    ) {
      return
    }

    this.init()
  }

  private init(): void {
    this.setupNavigationDrawerEventListeners()
    this.setupSearchDrawerEventListeners()
    this.setupCartDrawerEventListeners()
    if (this.navigationList) {
      this.setupNavigationItemEventListeners(this.navigationList, 0)
    }
    this.setupDrawerEventApi()
  }

  private setupDrawerEventApi(): void {
    this.drawerEventHandler = (e: Event) => {
      if (!(e instanceof CustomEvent)) return
      const name = e.detail?.drawer
      let drawer: HTMLDialogElement | null = null
      if (name === 'navigation') drawer = this.navigationDrawer
      else if (name === 'search') drawer = this.searchDrawer
      else if (name === 'cart') drawer = this.cartDrawer
      if (!drawer) return
      if (e.type === 'header:drawer-open') {
        drawer.showModal()
      } else if (e.type === 'header:drawer-close') {
        drawer.close()
      }
    }
    this.header.addEventListener(
      'header:drawer-open',
      this.drawerEventHandler,
    )
    this.header.addEventListener(
      'header:drawer-close',
      this.drawerEventHandler,
    )
  }

  private setupNavigationDrawerEventListeners(): void {
    this.navigationDrawer?.addEventListener('close', () => this.reset())
    this.navigationDrawer?.addEventListener('open', () => {
      this.searchDrawer?.close()
      this.cartDrawer?.close()
    })
  }

  private setupSearchDrawerEventListeners(): void {
    this.searchDrawer?.addEventListener('open', () => {
      if (this.navigationDrawer?.open) {
        this.navigationDrawer.close()
        this.reset()
      }
      this.cartDrawer?.close()
    })
  }

  private setupCartDrawerEventListeners(): void {
    this.cartDrawer?.addEventListener('open', () => {
      if (this.navigationDrawer?.open) {
        this.navigationDrawer.close()
        this.reset()
      }
      this.searchDrawer?.close()
    })
  }

  private async addExpansion(index: number): Promise<HTMLUListElement | null> {
    if (!this.navigationDrawer || !this.navigationDrawerBody) {
      return null
    }

    if (index + 1 >= DESKTOP_MAX_EXPANSIONS) {
      return null
    }

    if (this.expansions[index]) {
      const expansionChild = this.expansions[index]
        .firstElementChild as HTMLElement
      await animateLeave(expansionChild)
      this.expansions[index].innerHTML = ''

      if (index + 1 < this.expansions.length) {
        for (const [expansionIndex, expansion] of this.expansions.entries()) {
          if (expansionIndex > index) {
            expansion.remove()
          }
        }

        this.expansions.length = index + 1
      }

      return this.expansions[index]
    }

    const expansion = document.createElement('ul')
    expansion.setAttribute('role', 'list')
    expansion.classList.add('header__navigation-drawer-expansion')

    this.navigationDrawerBody.appendChild(expansion)
    this.expansions.push(expansion)

    return expansion
  }

  private setupNavigationItemEventListeners(
    root: HTMLElement,
    index: number,
  ): void {
    const navigationItemButtons = Array.from(
      root.querySelectorAll(
        '.header__navigation-item--active > .header__navigation-item-list > .header__navigation-item > button.header__navigation-item-link',
      ),
    )

    for (const navigationItemButton of navigationItemButtons) {
      if (!(navigationItemButton instanceof HTMLButtonElement)) {
        continue
      }

      const handler = async () => {
        const navigationItem = navigationItemButton.closest(
          '.header__navigation-item',
        )
        if (navigationItem) {
          const expansion = await this.addExpansion(index)
          if (expansion) {
            const cloned = navigationItem.cloneNode(true) as HTMLElement
            cloned.classList.add('header__navigation-item--active')
            prepareEnter(cloned)
            const appended = expansion.appendChild(cloned)
            const appendedList = appended.querySelector(
              '.header__navigation-item-list',
            ) as HTMLElement | null
            if (appendedList) {
              this.setupNavigationItemEventListeners(appendedList, index + 1)
            }

            const otherExpanded = navigationItem.parentNode?.querySelector(
              '[aria-expanded="true"]',
            )

            if (otherExpanded) {
              otherExpanded.setAttribute('aria-expanded', 'false')
            }

            navigationItemButton.setAttribute('aria-expanded', 'true')
            this.setTabOrder()
          }
        }
      }

      // Store handlers for ALL buttons, including cloned ones
      this.navigationButtonHandlers.set(
        navigationItemButton,
        handler.bind(this),
      )
      navigationItemButton.addEventListener('click', handler)
    }
  }

  private setTabOrder() {
    if (!this.navigationList || !this.navigationDrawer) {
      return
    }
    const metaItems: Array<HTMLButtonElement | HTMLAnchorElement> = Array.from(
      document.querySelectorAll(
        '.header__navigation-meta button, .header__navigation-meta a',
      ),
    )
    const close = this.navigationDrawer.querySelector(
      '.drawer__close',
    ) as HTMLButtonElement
    const order: Array<HTMLAnchorElement | HTMLButtonElement> = []

    const collectLinks = (
      container: Element,
      expansionIndex: number,
    ): void => {
      const links: Array<HTMLButtonElement | HTMLAnchorElement> = Array.from(
        container.querySelectorAll(
          ':scope > .header__navigation-item > .header__navigation-item-list > .header__navigation-item > .header__navigation-item-link',
        ),
      )
      for (const link of links) {
        order.push(link)
        if (
          link instanceof HTMLButtonElement &&
          link.getAttribute('aria-expanded') === 'true'
        ) {
          if (this.expansions && this.expansions[expansionIndex]) {
            collectLinks(this.expansions[expansionIndex], expansionIndex + 1)
          }
        }
      }
    }

    collectLinks(this.navigationList, 0)

    for (const metaItem of metaItems) {
      order.push(metaItem)
    }
    if (close) {
      order.push(close)
    }
    for (const [index, ordered] of order.entries()) {
      ordered.setAttribute('tabindex', (index + 1).toString())
    }
  }

  private resetTabOrder() {
    if (!this.navigationDrawer) {
      return
    }

    const tabbableItems = Array.from(
      this.navigationDrawer?.querySelectorAll(
        '.header__navigation-item--active .header__navigation-item-link',
      ),
    )

    if (tabbableItems && tabbableItems.length > 0) {
      for (const tabbableItem of tabbableItems) {
        tabbableItem.removeAttribute('tabindex')
        if (tabbableItem.getAttribute('aria-expanded')) {
          tabbableItem.setAttribute('aria-expanded', 'false')
        }
      }
    }
  }

  private reset() {
    for (const expansion of this.expansions) {
      expansion.remove()
    }
    this.expansions = []
    this.resetTabOrder()
  }

  destroy(): void {
    if (this.navigationDrawer) {
      this.navigationDrawer.close()
    }

    if (this.searchDrawer) {
      this.searchDrawer.close()
    }

    if (this.cartDrawer) {
      this.cartDrawer.close()
    }

    for (const [button, handler] of this.navigationButtonHandlers) {
      button.removeEventListener('click', handler)
    }
    this.navigationButtonHandlers.clear()

    this.reset()

    if (this.navigationDrawer) {
      const allNavigationButtons = Array.from(
        this.navigationDrawer.querySelectorAll(
          'button.header__navigation-item-link',
        ),
      )
      for (const button of allNavigationButtons) {
        button.replaceWith(button.cloneNode(true))
      }
    }

    if (this.drawerEventHandler) {
      this.header.removeEventListener(
        'header:drawer-open',
        this.drawerEventHandler,
      )
      this.header.removeEventListener(
        'header:drawer-close',
        this.drawerEventHandler,
      )
      this.drawerEventHandler = null
    }
  }
}

const BREAKPOINT = 992

let currentHeaderInstance: MobileHeader | DesktopHeader | null = null

function handleResponsive(e: MediaQueryListEvent | MediaQueryList): void {
  const header = document.querySelector('.header') as
    | HTMLElement & { showPopover: () => void }
    | null

  if (!header) return

  if (e.matches) {
    if (
      currentHeaderInstance && !(currentHeaderInstance instanceof DesktopHeader)
    ) {
      currentHeaderInstance.destroy()
      currentHeaderInstance = null
    }
    if (!currentHeaderInstance) {
      currentHeaderInstance = new DesktopHeader(header)
    }
  } else {
    if (
      currentHeaderInstance && !(currentHeaderInstance instanceof MobileHeader)
    ) {
      currentHeaderInstance.destroy()
      currentHeaderInstance = null
    }
    if (!currentHeaderInstance) {
      currentHeaderInstance = new MobileHeader(header)
    }
  }
}

export function initHeader(): void {
  const header = document.querySelector('.header') as HTMLElement | null
  if (!header) return
  if (header.dataset.hydrated) return
  header.dataset.hydrated = 'true'

  const isDesktop = window.matchMedia(`(min-width: ${BREAKPOINT}px)`)
  isDesktop.addEventListener('change', handleResponsive)
  handleResponsive(isDesktop)
}
